import {
  atom,
  atomFamily,
  DefaultValue,
  selector,
  selectorFamily
} from "recoil"
import { UserIdsSetterAction, UserModel } from "./types"
import { getUniqueList, getUpdatedPermission, userSorter } from "../../../util"
import { UserType } from "../../../constants/enums"
import { stopAudienceDisconnectTimer } from "../../../util/disconnectAudienceUser"

export const localUserStaticPropsAtom = atom({
  key: "localUserStaticProps",
  default: {
    id: "",
    name: ""
  }
})

const usersAtom = atomFamily({
  key: "users",
  default: null
})

const userIdsAtom = atom<string[]>({
  key: "userIds",
  default: []
})

const remoteUserIdsAtom = atom<string[]>({
  key: "remoteUserIds",
  default: []
})

export const localUserIdAtom = atom({
  key: "localUserIdAtom",
  default: null
})

export const localUserNameAtom = atom({
  key: "localUserNameAtom",
  default: ""
})

export const usersSelector = selectorFamily<UserModel, string>({
  key: "users-access-by-id",
  get:
    id =>
    ({ get }) => {
      return get(usersAtom(id))
    },
  set:
    id =>
    ({ set }, attributes) => {
      return set(usersAtom(id), user => ({
        ...user,
        ...attributes
      }))
    }
})

export const userIdsSelector = selector({
  key: "userIdsSelector",
  get: ({ get }) => {
    return get(userIdsAtom)
  },
  set: ({ set, reset, get }, action: any) => {
    if (action instanceof DefaultValue) {
      return false
    } else {
      const { name, users, userId, attributes } = action as UserIdsSetterAction

      if (name === "ADD") {
        const localUserId = get(localUserIdAtom)
        let isNewUserList = false

        // check isNewUserList by comparing localUser information is present in incoming users list.
        if(users.find(user => user.id === localUserId && !user.userSessionId)) {
          isNewUserList = true
        }

        let userArr = users
        let removePrevUserId = null
        let newRemoteUserIds = []
        const newUserIds = []
        for (let user of userArr) {
          if (
            user.type === UserType.BROADCASTER ||
            user.type === UserType.MODERATOR
          ) {
            stopAudienceDisconnectTimer()
          }
          newUserIds.push(user.id)
          let isLocalUser = localUserId === user.id
          
          // Add local user
          if (user.userSessionId) {

            // Remove previous local user - (Sometime Reconnect create new local user)
            if(user.id !== localUserId) {
              removePrevUserId = localUserId
              reset(usersAtom(removePrevUserId))
            }

            set(localUserIdAtom, user.id)
            isLocalUser = true
            set(usersAtom(user.id), user)
          } else if (localUserId !== user.id) {
            set(usersAtom(user.id), prevUser => {
              if (prevUser) {
                return { ...prevUser, ...user }
              }
              return user
            })
          }

          if (!isLocalUser) {
            // push remoteUserIdsAtom
            if (user?.camEnabled === true || user?.micEnabled === true) {
              newRemoteUserIds.push(user.id)              
            }
          }
        }


        // adding only unique ids
        if(isNewUserList) {
          // add all the new userIdsAtom
          set(userIdsAtom, newUserIds)
          set(remoteUserIdsAtom, newRemoteUserIds)
        } else {
          // Add the user id if not already present
          set(userIdsAtom, prevIds => {
            const prevUserIds = prevIds.filter(id => id !== removePrevUserId )
            return Array.from(new Set([...prevUserIds, ...newUserIds]))
          })

          set(remoteUserIdsAtom, prevIds => {
            return getUniqueList([...prevIds, ...newRemoteUserIds])
          })

        }
      }

      if (name === "UPDATE") {
        set(usersAtom(userId), user => ({ ...user, ...attributes }))

        if (attributes?.handUp !== undefined) {
          return
        }

        if (attributes?.muted !== undefined) {
          return
        }

        // for remoteUserIdsAtom
        const existingRemoteUserIds = get(remoteUserIdsAtom)
        const localUserId = get(localUserIdAtom)
        let isLocalUser = false
        if (localUserId === userId) {
          isLocalUser = true
        }
        if (!isLocalUser) {
          let changedUserId = userId
          if (userId.substring(0, 2) === "ss") {
            changedUserId = userId?.replace("ss", "")
          } else if (userId.substring(0, 2) === "wp") {
            changedUserId = userId?.replace("wp", "")
          }

          if (existingRemoteUserIds.indexOf(changedUserId) === -1) {
            // if not existing in remote user ids list
            if (
              attributes?.micEnabled === true ||
              attributes?.camEnabled === true
            ) {
              set(remoteUserIdsAtom, userIds => [...userIds, changedUserId])
            }
          } else {
            const existingRemoteUserData = get(usersAtom(changedUserId))
            let shouldExistInRemoteUsersList = false
            if (
              attributes["micEnabled"] !== undefined &&
              attributes["micEnabled"] === true
            ) {
              shouldExistInRemoteUsersList = true
            } else if (
              attributes["camEnabled"] !== undefined &&
              attributes["camEnabled"] === true
            ) {
              shouldExistInRemoteUsersList = true
            } else if (
              attributes["micEnabled"] !== undefined &&
              attributes["micEnabled"] !== true
            ) {
              if (existingRemoteUserData?.camEnabled === true) {
                shouldExistInRemoteUsersList = true
              }
            } else if (
              attributes["camEnabled"] !== undefined &&
              attributes["camEnabled"] !== true
            ) {
              if (existingRemoteUserData?.micEnabled === true) {
                shouldExistInRemoteUsersList = true
              }
            } else if (
              attributes["isPublishingMic"] !== undefined &&
              attributes["isPublishingMic"] === true
            ) {
              shouldExistInRemoteUsersList = true
            } else if (
              attributes["camEnabled"] !== undefined &&
              attributes["camEnabled"] === true
            ) {
              shouldExistInRemoteUsersList = true
            } else if (
              attributes["micEnabled"] !== undefined &&
              attributes["micEnabled"] !== true
            ) {
              if (existingRemoteUserData?.camEnabled === true) {
                shouldExistInRemoteUsersList = true
              }
            } else if (
              attributes["camEnabled"] !== undefined &&
              attributes["camEnabled"] !== true
            ) {
              if (existingRemoteUserData?.micEnabled === true) {
                shouldExistInRemoteUsersList = true
              }
            } else if (attributes["lastSpeakTime"] !== undefined) {
              // if some user is speaking, it means his MIC is enabled
              shouldExistInRemoteUsersList = true
            } else if (attributes["screenShareEnabled"] !== undefined) {
              if (
                existingRemoteUserData?.camEnabled === true ||
                existingRemoteUserData?.micEnabled === true
              ) {
                shouldExistInRemoteUsersList = true
              }
            } else if (attributes["permissions"] !== undefined) {
              if (
                existingRemoteUserData?.camEnabled === true ||
                existingRemoteUserData?.micEnabled === true
              ) {
                shouldExistInRemoteUsersList = true
              }
            }

            if (shouldExistInRemoteUsersList) {
              // add to remote users list if not already exist
              const existingRemoteUserIds = get(remoteUserIdsAtom)
              if (existingRemoteUserIds.indexOf(changedUserId) === -1) {
                set(remoteUserIdsAtom, userIds => [...userIds, changedUserId])
              }
            } else {
              // delete from remote users list
              set(remoteUserIdsAtom, prev =>
                prev.filter(uid => uid !== changedUserId)
              )
            }
          }
        }
      }

      if (name === "UPDATE_ALL") {
        set(userIdsAtom, prev => {
          const list = prev.map(el => {
            set(usersAtom(el), user => {
              if (user.type === UserType?.AUDIENCE) {
                if (
                  attributes.permissions !== undefined ||
                  attributes.addPermissions !== undefined ||
                  attributes.removePermissions !== undefined
                ) {
                  // Update the permissions for all audience users
                  const newPermissions = getUpdatedPermission(
                    user.permissions,
                    attributes.permissions,
                    attributes.addPermissions,
                    attributes.removePermissions
                  )
                  return {
                    ...user,
                    oldPermissions: user.permissions,
                    permissions: newPermissions
                  }
                } else {
                  return { ...user, ...attributes }
                }
              }
              return user
            })
            return el
          })
          return list
        })
      }

      if (name === "UPDATE_ALL_BROADCASTER") {
        set(userIdsAtom, prev => {
          const list = prev.map(el => {
            set(usersAtom(el), user => {
              if (user.type === UserType?.BROADCASTER) {
                if (
                  attributes.permissions !== undefined ||
                  attributes.addPermissions !== undefined ||
                  attributes.removePermissions !== undefined
                ) {
                  const newPermissions = getUpdatedPermission(
                    user.permissions,
                    attributes.permissions,
                    attributes.addPermissions,
                    attributes.removePermissions
                  )
                  // Update the permissions for all broadcaster users
                  return {
                    ...user,
                    oldPermissions: user.permissions,
                    permissions: newPermissions
                  }
                } else {
                  return { ...user, ...attributes }
                }
              }
              return user
            })
            return el
          })
          return list
        })
      }

      if (name === "REMOVE") {
        let userArr = users
        for (let user of userArr) {
          reset(usersAtom(user.id))
          set(userIdsAtom, prev => prev.filter(uid => uid !== user.id))
          set(remoteUserIdsAtom, prev => prev.filter(uid => uid !== user.id))
        }
      }

      if (name === "RESET") {
        let userArr = get(userIdsAtom)
        for (let user of userArr) {
          reset(usersAtom(user))
        }
        set(userIdsAtom, [])
        set(remoteUserIdsAtom, [])
      }
    }
  }
})

//This is User Object List used in the Participants List
export const userListSelector = selector({
  key: "userListSelector",
  get: ({ get }) => {
    const userIdsList = get(userIdsAtom)
    return userIdsList.map(uid => get(usersAtom(uid))).sort(userSorter)
  }
})

export const userListUpHandSelector = selector({
  key: "userListUpHandSelector",
  get: ({ get }) => {
    const userIdsList = get(userIdsAtom)
    return userIdsList.map(uid => get(usersAtom(uid))).some(el => el.handUp)
  }
})

//This is User Object List used in the Participants List
export const allUsersCountSelector = selector({
  key: "allUsersCountSelector",
  get: ({ get }) => {
    const userIdsList = get(userIdsAtom)
    let userCount = 0
    if (userIdsList.length) {
      userCount = userIdsList.length
    }
    return userCount
  }
})

export const remoteUserIdsSelector = selector({
  key: "remoteUserIdsSelector",
  get: ({ get }) => {
    return get(remoteUserIdsAtom)
  }
})

export const remoteUsersListSelector = selector({
  key: "remoteUsersListSelector",
  get: ({ get }) => {
    const remoteUserIdsList = get(remoteUserIdsAtom)
    return remoteUserIdsList.map(uid => get(usersAtom(uid)))
  }
})

interface LocalUserCallbackSelectorProps {
  handleReturnData?: () => Promise<UserModel>
}

export const localUserCallbackSelector =
  selector<LocalUserCallbackSelectorProps>({
    key: "localUserCallbackSelector",
    get: ({ getCallback, get }) => {
      const handleReturnData = getCallback(({ snapshot }) => async () => {
        const data = await snapshot.getPromise(localUserIdAtom)

        const user = await snapshot.getLoadable(usersAtom(data)).contents

        return user
      })
      return { handleReturnData }
    }
  })

export const userActionsSelector = selector({
  key: "userActionsSelector",
  get: ({ getCallback }) => {
    const getUserDataById = getCallback(
      ({ snapshot }) =>
        async (id: string | number) => {
          const data = await snapshot.getPromise(usersAtom(id))

          return data as UserModel
        }
    )

    const getUserCount = getCallback(({ snapshot }) => async () => {
      const data = await snapshot.getPromise(userIdsAtom)
      return data.length
    })

    return { getUserDataById, getUserCount }
  }
})
